import { useEffect, useState } from "react"
import api from "../../lib/api"
import { toastError } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"
import DataTable, { type DataTableColumn, type DataTableSort } from "../../components/ui/table/DataTable"
import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import { hasCurrentPolicy } from "../../lib/auth"

type SectionQuizOverviewRow = {
  id: string
  title: string
  status: "draft" | "generating" | "active" | "error"
  attempt_mode: "individual" | "team"
  passing_score: number
  attempt_count: number
  course_id: string
  course_title: string
  section_id: string
  section_title: string
}

const STATUS_BADGE: Record<string, string> = {
  active: "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300",
  generating: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  error: "bg-error-100 text-error-700 dark:bg-error-500/15 dark:text-error-300",
  draft: "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
}
const STATUS_LABEL: Record<string, string> = {
  active: "Activo",
  generating: "Generando...",
  error: "Error",
  draft: "Borrador",
}

const PAGE_SIZE = 20

export default function SectionQuizzesOverviewPage() {
  const [quizzes, setQuizzes] = useState<SectionQuizOverviewRow[]>([])
  const [loading, setLoading] = useState(true)
  const [sort, setSort] = useState<DataTableSort>({ column: "course_title", dir: "asc" })
  const [page, setPage] = useState(1)

  useEffect(() => {
    api
      .get("/academy/section-quizzes/admin/all")
      .then((res) => setQuizzes(res.data.data?.quizzes ?? []))
      .catch((err) => toastError(err))
      .finally(() => setLoading(false))
  }, [])

  const activeCount = quizzes.filter((q) => q.status === "active").length
  const totalAttempts = quizzes.reduce((sum, q) => sum + q.attempt_count, 0)
  const teamCount = quizzes.filter((q) => q.attempt_mode === "team").length

  const sorted = [...quizzes].sort((a, b) => {
    if (!sort) return 0
    const dir = sort.dir === "asc" ? 1 : -1
    switch (sort.column) {
      case "course_title":
        return (a.course_title + a.section_title).localeCompare(b.course_title + b.section_title) * dir
      case "attempt_count":
        return (a.attempt_count - b.attempt_count) * dir
      default:
        return 0
    }
  })
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const columns: DataTableColumn<SectionQuizOverviewRow>[] = [
    {
      key: "course_title",
      label: "Curso / Sección",
      sortable: true,
      render: (q) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{q.course_title}</p>
          <p className="text-xs text-gray-500">{q.section_title} — {q.title}</p>
        </div>
      ),
    },
    {
      key: "status",
      label: "Estado",
      render: (q) => (
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_BADGE[q.status]}`}>
          {STATUS_LABEL[q.status]}
        </span>
      ),
    },
    {
      key: "attempt_mode",
      label: "Modo",
      render: (q) => (
        <span className="text-gray-600 dark:text-gray-300">
          {q.attempt_mode === "team" ? "Equipo CCCD" : "Individual"}
        </span>
      ),
    },
    {
      key: "attempt_count",
      label: "Intentos",
      sortable: true,
      render: (q) => String(q.attempt_count),
    },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (q) => (
        <RowActionsMenu
          label="Acciones del examen"
          actions={[
            {
              label: "Ver intentos",
              icon: <span>👁</span>,
              to: `/instructor/section-quizzes/${q.id}/attempts`,
            },
            // Probar el examen como un estudiante (solo personal; no afecta datos reales).
            ...(hasCurrentPolicy("academy:quizzes-teach:edit")
              ? [
                  ...(q.status === "active"
                    ? [{ label: "Probar examen", icon: <span>🧪</span>, to: `/instructor/section-quizzes/${q.id}/test` }]
                    : []),
                  { label: "Pruebas", icon: <span>📋</span>, to: `/instructor/section-quizzes/${q.id}/tests` },
                ]
              : []),
          ]}
        />
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Contenido", "Exámenes IA"]}
      title="Exámenes IA por sección"
      subtitle="Todos los exámenes generados por IA, en todos los cursos — entra a cada uno para ver quién respondió qué"
      tabs={[
        { label: "Panel", to: "/instructor/hub" },
        { label: "Entregas", to: "/instructor/submissions" },
        { label: "Exámenes IA", to: "/instructor/section-quizzes", active: true },
        { label: "Estudiantes", to: "/instructor/students" },
      ]}
      metrics={[
        { label: "Exámenes activos", value: String(activeCount) },
        { label: "De equipo (CCCD)", value: String(teamCount) },
        { label: "Intentos totales", value: String(totalAttempts) },
      ]}
    >
      <DataTable
        columns={columns}
        rows={pageRows}
        rowKey={(q) => q.id}
        loading={loading}
        emptyMessage="Ningún curso tiene un examen IA activado todavía. Actívalos desde el editor de curso, en cada sección."
        sort={sort}
        onSortChange={(next) => { setSort(next); setPage(1) }}
        page={page}
        pageSize={PAGE_SIZE}
        total={sorted.length}
        onPageChange={setPage}
      />
    </ModuleLayout>
  )
}
